import { mountNav } from '../components/Header';
import { getPaste, updatePaste } from '../api/client';
import { attachLineNumbers } from '../components/lineNumbers';

const EXPIRY_OPTIONS: Array<[string, string]> = [
    ['10m', '10 minutes'],
    ['1h', '1 hour'],
    ['24h', '24 hours'],
    ['7d', '7 days'],
    ['30d', '30 days'],
    ['never', 'Never'],
];

const VISIBILITY_OPTIONS: Array<[string, string]> = [
    ['public', 'Public'],
    ['unlisted', 'Unlisted'],
    ['private', 'Private'],
    ['private_encrypted', 'Encrypted'],
];

function expiryOptionSelected(expiresAt: unknown, value: string): boolean {
    if (!expiresAt) return value === 'never';
    const exp = new Date(expiresAt as string).getTime();
    const now = Date.now();
    if (value === 'never') return false;
    const M = 60 * 1000;
    const H = 60 * M;
    const D = 24 * H;
    const buckets: Array<[string, number]> = [
        ['10m', 10 * M],
        ['1h', H],
        ['24h', 24 * H],
        ['7d', 7 * D],
        ['30d', 30 * D],
    ];
    const diff = exp - now;
    if (diff <= 0) return value === 'never';
    const closest = buckets.reduce((best, [k, ms]) => {
        return Math.abs(ms - diff) < Math.abs(best[1] - diff) ? [k, ms] as [string, number] : best;
    }, buckets[0]);
    return value === closest[0];
}

export function EditPastePage(id: string): void {
    const app = document.getElementById('app');
    if (!app) return;
    app.innerHTML = `
        <div class="container">
            <header class="header"><h1>Mediabin</h1><nav id="nav"></nav></header>
            <main class="main" id="edit-container"><div class="loading">Loading...</div></main>
        </div>
    `;
    mountNav();
    loadForEdit(id);
}

async function loadForEdit(id: string): Promise<void> {
    const container = document.getElementById('edit-container');
    if (!container) return;
    try {
        const paste = await getPaste(id);
        const isText = paste.mode === 'text' && !paste.is_encrypted;
        const visOptions = VISIBILITY_OPTIONS.map(([v, label]) =>
            `<option value="${v}" ${paste.visibility === v ? 'selected' : ''}>${label}</option>`).join('');
        const expOptions = EXPIRY_OPTIONS.map(([v, label]) =>
            `<option value="${v}" ${expiryOptionSelected(paste.expires_at, v) ? 'selected' : ''}>${label}</option>`).join('');
        container.innerHTML = `
            <h2>Edit Paste ${id}</h2>
            <form id="edit-paste-form">
                <div class="option-group">
                    <label>Visibility</label>
                    <select id="vis">${visOptions}</select>
                </div>
                <div class="option-group">
                    <label>Expiry</label>
                    <select id="exp">${expOptions}</select>
                </div>
                <div class="option-group checkbox-group">
                    <label><input type="checkbox" id="burn" ${paste.burn_after_read ? 'checked' : ''}> Burn after read</label>
                </div>
                <div class="option-group">
                    <label>Password (blank = unchanged)</label>
                    <input type="password" id="pw" placeholder="New password">
                </div>
                <div class="option-group checkbox-group">
                    <label><input type="checkbox" id="clear-pw"> Clear password</label>
                </div>
                ${isText ? `<div class="option-group"><label>Content</label><div class="editor-wrap"><div class="line-numbers" aria-hidden="true"></div><textarea id="content" rows="10">${escapeHtml(String(paste.content ?? ''))}</textarea></div></div>` : ''}
                <div class="actions">
                    <button class="btn btn-primary" type="submit">Save</button>
                </div>
                <p id="edit-error" class="error-msg" style="display: none;"></p>
            </form>
        `;
        const editTextarea = document.getElementById('content') as HTMLTextAreaElement | null;
        const editGutter = container.querySelector('.line-numbers') as HTMLDivElement | null;
        if (editTextarea && editGutter) {
            attachLineNumbers(editTextarea, editGutter);
        }
        const form = document.getElementById('edit-paste-form');
        form?.addEventListener('submit', async (e) => {
            e.preventDefault();
            await saveEdit(id);
        });
    } catch (error) {
        container.innerHTML = `<div class="error"><p>${error instanceof Error ? error.message : 'Failed to load paste'}</p></div>`;
    }
}

async function saveEdit(id: string): Promise<void> {
    const err = document.getElementById('edit-error');
    const exp = (document.getElementById('exp') as HTMLSelectElement).value;
    const body: Record<string, unknown> = {
        visibility: (document.getElementById('vis') as HTMLSelectElement).value,
        expires_in: exp === 'never' ? null : exp,
        burn_after_read: (document.getElementById('burn') as HTMLInputElement).checked,
    };
    const clearPw = (document.getElementById('clear-pw') as HTMLInputElement).checked;
    if (clearPw) {
        body.clear_password = true;
    } else {
        const pw = (document.getElementById('pw') as HTMLInputElement).value;
        if (pw) body.password = pw;
    }
    const content = document.getElementById('content') as HTMLTextAreaElement | null;
    if (content) body.content = content.value;

    try {
        await updatePaste(id, body);
        window.location.href = '/me';
    } catch (error) {
        if (err) {
            err.style.display = 'block';
            err.textContent = error instanceof Error ? error.message : 'Save failed';
        }
    }
}

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
